import { useState, useMemo, useRef, useEffect, useDeferredValue } from 'react';
import { Search, X, SlidersHorizontal, Loader2, ArrowUpDown, Flame, Hash } from 'lucide-react';
import { VirtualizedCardGrid } from '@/components/VirtualizedCardGrid';
import { CardImage } from '@/components/CardImage';
import { InkDot } from '@/components/InkDot';
import { CardSkeleton } from '@/components/CardSkeleton';
import { CardDetailSheet } from '@/components/CardDetailSheet';
import { TreatmentPicker } from '@/components/TreatmentPicker';
import { useCards, useCardSets, useAllPrints, getBestPrintPrice, type Card } from '@/hooks/useCards';
import { useAddToCollection, useCollection, type CardPrint } from '@/hooks/useCollection';
import { useWishlist, useAddToWishlist, useRemoveFromWishlist } from '@/hooks/useWishlist';
import { useCardPopularity } from '@/hooks/useCardPopularity';
import { useAuth } from '@/hooks/useAuth';
import { formatPrice } from '@/lib/card-prices';
import { treatmentLabel } from '@/lib/treatment';
import type { InkColor } from '@/lib/ink-utils';
import { toast } from 'sonner';

const INK_COLORS = ['All', 'Amber', 'Amethyst', 'Emerald', 'Ruby', 'Sapphire', 'Steel'] as const;
const RARITIES = ['All', 'Common', 'Uncommon', 'Rare', 'Super Rare', 'Epic', 'Legendary', 'Iconic', 'Enchanted'] as const;
const CARD_TYPES = ['All', 'Character', 'Action', 'Item', 'Location', 'Song'] as const;
const SORT_OPTIONS = [
  { value: 'popular', label: '🔥 Popular' },
  { value: 'number', label: '# Number' },
  { value: 'name', label: 'Name' },
  { value: 'set', label: 'Set' },
  { value: 'price_desc', label: 'Price ↓' },
  { value: 'price_asc', label: 'Price ↑' },
  { value: 'rarity', label: 'Rarity' },
] as const;

const RARITY_ORDER: Record<string, number> = {
  'Enchanted': 7, 'Legendary': 6, 'Super Rare': 5, 'Epic': 4, 'Rare': 3, 'Uncommon': 2, 'Common': 1, 'Special': 4, 'Iconic': 5,
};

const PAGE_SIZE = 60;

export function LibraryTab() {
  const { user } = useAuth();
  const [search, setSearch] = useState('');
  const [inkFilter, setInkFilter] = useState<string>('All');
  const [rarityFilter, setRarityFilter] = useState<string>('All');
  const [typeFilter, setTypeFilter] = useState<string>('All');
  const [setFilter, setSetFilter] = useState<string>('All');
  const [sortBy, setSortBy] = useState<string>('popular');
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState<'grouped' | 'sets'>('grouped');
  const groupedView = true;
  const [selectedCard, setSelectedCard] = useState<Card | null>(null);
  const [addingCard, setAddingCard] = useState<Card | null>(null);
  const [addingPrintId, setAddingPrintId] = useState<string | null>(null);
  const [addingPrint, setAddingPrint] = useState<CardPrint | null>(null);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  // Fetch the whole card catalog ONCE and filter client-side. This avoids a
  // network roundtrip on every keystroke / filter change.
  const { data: cards, isLoading } = useCards();

  const { data: allSets } = useCardSets();
  const { data: collection } = useCollection();
  const { data: wishlist } = useWishlist();
  const { data: allPrintsByCard } = useAllPrints();
  const { data: popularity } = useCardPopularity();
  const addToCollection = useAddToCollection();
  const addToWishlist = useAddToWishlist();
  const removeFromWishlist = useRemoveFromWishlist();

  // Defer search so heavy memoized work doesn't run on every keystroke.
  const deferredSearch = useDeferredValue(search);

  const getRealPrice = (card: Card): number | null =>
    getBestPrintPrice(allPrintsByCard?.get(card.id));

  const normalizeKey = (c: Card) =>
    `${(c.name || '').toLowerCase().replace(/[’']/g, "'").trim()}|${(c.subtitle || '').toLowerCase().trim()}`;

  const grouped = useMemo(() => {
    let filtered = cards || [];
    const q = deferredSearch.trim().toLowerCase();
    if (q || inkFilter !== 'All' || rarityFilter !== 'All' || setFilter !== 'All' || typeFilter !== 'All') {
      filtered = filtered.filter(c => {
        if (inkFilter !== 'All' && c.ink_color !== inkFilter) return false;
        if (rarityFilter !== 'All' && c.rarity !== rarityFilter) return false;
        if (setFilter !== 'All' && c.set_name !== setFilter) return false;
        if (typeFilter !== 'All' && c.card_type !== typeFilter) return false;
        if (q) {
          const hay = `${c.name || ''} ${c.subtitle || ''}`.toLowerCase();
          if (!hay.includes(q)) return false;
        }
        return true;
      });
    }

    if (!groupedView) {
      return filtered.map(c => ({ rep: c, siblings: [c] as Card[] }));
    }

    const map = new Map<string, { rep: Card; siblings: Card[] }>();
    for (const c of filtered) {
      const key = normalizeKey(c);
      const entry = map.get(key);
      if (!entry) {
        map.set(key, { rep: c, siblings: [c] });
      } else {
        entry.siblings.push(c);
        const cp = getRealPrice(c);
        const rp = getRealPrice(entry.rep);
        if (cp != null && (rp == null || cp < rp)) entry.rep = c;
      }
    }

    return Array.from(map.values());
  }, [cards, deferredSearch, inkFilter, rarityFilter, setFilter, typeFilter, allPrintsByCard, groupedView]);



  // Per-rarity prices for a group. Only counts real prices.
  const getGroupPrices = (g: { rep: Card; siblings: Card[] }) => {
    const byRarity = new Map<string, number>();
    let min = Infinity;
    let max = -Infinity;
    for (const s of g.siblings) {
      const price = getRealPrice(s);
      if (price == null) continue;
      if (price < min) min = price;
      if (price > max) max = price;
      const prev = byRarity.get(s.rarity);
      if (prev == null || price < prev) byRarity.set(s.rarity, price);
    }
    if (!isFinite(min)) return { min: null as number | null, max: null as number | null, byRarity };
    return { min, max, byRarity };
  };

  // Compute popularity score per group (memoized).
  const popularityScore = useMemo(() => {
    if (!popularity || grouped.length === 0) return null;
    const rawScores = new Map<string, number>();
    let maxPrice = 0, maxOwn = 0, maxMen = 0;
    const tmp: { key: string; price: number; own: number; men: number }[] = [];
    for (const g of grouped) {
      let own = 0, men = 0;
      for (const s of g.siblings) {
        own += popularity.ownership.get(s.id) || 0;
        men += popularity.mentions.get(s.id) || 0;
      }
      const price = getRealPrice(g.rep) ?? 0;
      const k = normalizeKey(g.rep);
      tmp.push({ key: k, price, own, men });
      if (price > maxPrice) maxPrice = price;
      if (own > maxOwn) maxOwn = own;
      if (men > maxMen) maxMen = men;
    }
    for (const r of tmp) {
      const p = maxPrice ? r.price / maxPrice : 0;
      const o = maxOwn ? r.own / maxOwn : 0;
      const m = maxMen ? r.men / maxMen : 0;
      rawScores.set(r.key, 0.5 * p + 0.3 * o + 0.2 * m);
    }
    return rawScores;
  }, [grouped, popularity, allPrintsByCard]);

  const sorted = useMemo(() => {
    const arr = [...grouped];
    switch (sortBy) {
      case 'popular':
        arr.sort((a, b) => {
          const sa = popularityScore?.get(normalizeKey(a.rep)) ?? 0;
          const sb = popularityScore?.get(normalizeKey(b.rep)) ?? 0;
          if (sb !== sa) return sb - sa;
          return a.rep.name.localeCompare(b.rep.name);
        });
        break;
      case 'set':
        arr.sort((a, b) => a.rep.set_name.localeCompare(b.rep.set_name) || a.rep.name.localeCompare(b.rep.name));
        break;
      case 'number':
        arr.sort((a, b) => {
          const setCmp = a.rep.set_name.localeCompare(b.rep.set_name);
          if (setCmp !== 0) return setCmp;
          const na = parseInt(a.rep.set_number || '', 10);
          const nb = parseInt(b.rep.set_number || '', 10);
          const aNum = isNaN(na) ? Infinity : na;
          const bNum = isNaN(nb) ? Infinity : nb;
          if (aNum !== bNum) return aNum - bNum;
          return (a.rep.set_number || '').localeCompare(b.rep.set_number || '');
        });
        break;
      case 'price_desc':
        arr.sort((a, b) => (getGroupPrices(b).max ?? 0) - (getGroupPrices(a).max ?? 0));
        break;
      case 'price_asc':
        arr.sort((a, b) => (getGroupPrices(a).min ?? Infinity) - (getGroupPrices(b).min ?? Infinity));
        break;
      case 'rarity':
        arr.sort((a, b) => (RARITY_ORDER[b.rep.rarity] || 0) - (RARITY_ORDER[a.rep.rarity] || 0));
        break;
      default:
        arr.sort((a, b) => a.rep.name.localeCompare(b.rep.name));
    }
    return arr;
  }, [grouped, sortBy, popularityScore]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [search, inkFilter, rarityFilter, typeFilter, setFilter, sortBy, viewMode]);

  const visibleCards = useMemo(() => sorted.slice(0, visibleCount), [sorted, visibleCount]);
  const hasMore = visibleCount < sorted.length;

  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!sentinelRef.current || !hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisibleCount(prev => Math.min(prev + PAGE_SIZE, sorted.length));
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, sorted.length]);

  const activeFilterCount = [inkFilter, rarityFilter, typeFilter, setFilter].filter(f => f !== 'All').length;
  const collectionCardIds = new Set((collection || []).map(c => c.card_id));
  const wishlistCardIds = new Set((wishlist || []).map(w => w.card_id));

  const getOwnedQty = (cardId: string) => {
    const item = (collection || []).find(c => c.card_id === cardId);
    return item ? (item.quantity || 1) : 0;
  };

  const handleAddToCollection = (card: Card) => {
    if (!user) { toast.error('Sign in to add cards'); return; }
    setAddingCard(card);
    setAddingPrintId(null);
    setAddingPrint(null);
  };

  const confirmAdd = async () => {
    if (!addingCard) return;
    try {
      await addToCollection.mutateAsync({ cardId: addingCard.id, printId: addingPrintId });
      const label = addingPrint ? treatmentLabel(addingPrint.treatment) : 'Normal';
      toast.success(`${addingCard.name} (${label}) added to collection! ✨`);
      setAddingCard(null);
      setAddingPrintId(null);
      setAddingPrint(null);
    } catch (err: any) { toast.error(err.message); }
  };

  const handleToggleWishlist = async (card: Card) => {
    if (!user) { toast.error('Sign in to use wishlist'); return; }
    try {
      if (wishlistCardIds.has(card.id)) {
        await removeFromWishlist.mutateAsync(card.id);
        toast.success(`Removed from wishlist`);
      } else {
        await addToWishlist.mutateAsync({ cardId: card.id });
        toast.success(`${card.name} added to wishlist! ❤️`);
      }
    } catch (err: any) { toast.error(err.message); }
  };

  const selectedGroup = selectedCard
    ? grouped.find(g => g.siblings.some(s => s.id === selectedCard.id))
    : null;
  const selectedOwned = selectedGroup?.siblings.some(s => collectionCardIds.has(s.id)) ?? false;
  const selectedWishlisted = selectedGroup?.siblings.some(s => wishlistCardIds.has(s.id)) ?? false;
  const selectedOwnedQty = selectedCard ? getOwnedQty(selectedCard.id) : 0;

  return (
    <div className="px-4 pb-4">
      {/* Search */}
      <div className="flex gap-2 mt-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search all Lorcana cards..."
            aria-label="Search all Lorcana cards"
            className="w-full rounded-lg border border-border bg-card py-2.5 pl-9 pr-3 text-sm text-foreground font-body placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          />
          {search && (
            <button onClick={() => setSearch('')} aria-label="Clear search" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          aria-label={showFilters ? 'Hide filters' : 'Show filters'}
          aria-expanded={showFilters}
          className={`relative flex h-10 w-10 items-center justify-center rounded-lg border transition-colors ${showFilters ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-card text-muted-foreground'}`}
        >
          <SlidersHorizontal className="h-4 w-4" />
          {activeFilterCount > 0 && (
            <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[9px] font-bold text-primary-foreground">
              {activeFilterCount}
            </span>
          )}
        </button>
        <div className="flex items-center rounded-lg border border-border bg-card overflow-hidden h-10">
          <button
            onClick={() => setViewMode('sets')}
            title="Popular cards grouped by set"
            className={`px-2.5 h-full text-[10px] font-medium font-body transition-colors ${viewMode === 'sets' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
          >
            By Set
          </button>
          <button
            onClick={() => setViewMode('grouped')}
            className={`px-2.5 h-full text-[10px] font-medium font-body transition-colors ${viewMode === 'grouped' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
          >
            Grouped
          </button>
        </div>
      </div>

      {/* Filters panel */}
      {showFilters && (
        <div className="mt-2 rounded-xl border border-border bg-card p-3 space-y-3 animate-fade-in">
          <div>
            <p className="text-[10px] text-muted-foreground font-body mb-1.5 uppercase tracking-wider">Ink Color</p>
            <div className="flex flex-wrap gap-1.5">
              {INK_COLORS.map(ink => (
                <button key={ink} onClick={() => setInkFilter(ink)} className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-medium font-body transition-colors ${inkFilter === ink ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground'}`}>
                  {ink !== 'All' && <InkDot ink={ink as InkColor} size="sm" />}
                  {ink}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-[10px] text-muted-foreground font-body mb-1.5 uppercase tracking-wider">Rarity</p>
            <div className="flex flex-wrap gap-1.5">
              {RARITIES.map(r => (
                <button key={r} onClick={() => setRarityFilter(r)} className={`rounded-full px-2.5 py-1 text-[10px] font-medium font-body transition-colors ${rarityFilter === r ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground'}`}>
                  {r}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-[10px] text-muted-foreground font-body mb-1.5 uppercase tracking-wider">Card Type</p>
            <div className="flex flex-wrap gap-1.5">
              {CARD_TYPES.map(t => (
                <button key={t} onClick={() => setTypeFilter(t)} className={`rounded-full px-2.5 py-1 text-[10px] font-medium font-body transition-colors ${typeFilter === t ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground'}`}>
                  {t}
                </button>
              ))}
            </div>
          </div>
          {allSets && allSets.length > 0 && (
            <div>
              <p className="text-[10px] text-muted-foreground font-body mb-1.5 uppercase tracking-wider">Set</p>
              <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
                <button onClick={() => setSetFilter('All')} className={`rounded-full px-2.5 py-1 text-[10px] font-medium font-body transition-colors ${setFilter === 'All' ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground'}`}>All</button>
                {allSets.map(s => (
                  <button key={s} onClick={() => setSetFilter(s)} className={`rounded-full px-2.5 py-1 text-[10px] font-medium font-body transition-colors truncate max-w-[120px] ${setFilter === s ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground'}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div>
            <p className="text-[10px] text-muted-foreground font-body mb-1.5 uppercase tracking-wider">Sort By</p>
            <div className="flex flex-wrap gap-1.5">
              {SORT_OPTIONS.map(opt => (
                <button key={opt.value} onClick={() => setSortBy(opt.value)} className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-medium font-body transition-colors ${sortBy === opt.value ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground'}`}>
                  {opt.value === sortBy && <ArrowUpDown className="h-2.5 w-2.5" />}
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          {activeFilterCount > 0 && (
            <button onClick={() => { setInkFilter('All'); setRarityFilter('All'); setTypeFilter('All'); setSetFilter('All'); }} className="text-xs text-primary font-body underline">
              Clear all filters
            </button>
          )}
        </div>
      )}

      {/* Popularity / Number toggle */}
      <div className="mt-3 flex items-center gap-2">
        <div className="flex items-center rounded-lg border border-border bg-card overflow-hidden h-8">
          <button
            onClick={() => setSortBy('popular')}
            className={`flex items-center gap-1 px-3 h-full text-[10px] font-medium font-body transition-colors ${sortBy === 'popular' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
          >
            <Flame className="h-3 w-3" />
            Popular
          </button>
          <div className="w-px h-4 bg-border" />
          <button
            onClick={() => setSortBy('number')}
            className={`flex items-center gap-1 px-3 h-full text-[10px] font-medium font-body transition-colors ${sortBy === 'number' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
          >
            <Hash className="h-3 w-3" />
            Number
          </button>
        </div>
        <p className="text-[10px] text-muted-foreground font-body">
          {isLoading ? 'Loading...' : `${sorted.length} card${sorted.length !== 1 ? 's' : ''}`}
        </p>
      </div>

      {(() => {
        const renderCard = (group: { rep: Card; siblings: Card[] }, idx: number, opts?: { showRank?: boolean }) => {
          const card = group.rep;
          const isFoil = (group as any).foil === true;
          const foilTreatment: string | undefined = (group as any).foilTreatment;
          let priceLabel = '—';
          let sameRarities = 0;
          if (isFoil) {
            let foilPrice: number | null = null;
            for (const s of group.siblings) {
              const prints = allPrintsByCard?.get(s.id);
              const hit = prints?.find(p => p.treatment === foilTreatment && p.price_usd != null);
              if (hit?.price_usd != null) {
                const v = Number(hit.price_usd);
                if (foilPrice == null || v < foilPrice) foilPrice = v;
              }
            }
            priceLabel = foilPrice != null ? formatPrice(foilPrice) : '—';
          } else {
            const { min, max, byRarity } = getGroupPrices(group);
            sameRarities = byRarity.size;
            priceLabel = min == null
              ? '—'
              : min === max ? formatPrice(min) : `${formatPrice(min)}–${formatPrice(max!)}`;
          }
          const owned = group.siblings.some(s => collectionCardIds.has(s.id));
          const wishlisted = group.siblings.some(s => wishlistCardIds.has(s.id));
          const isTopPopular = !!opts?.showRank && idx < 3;
          const foilLabel = foilTreatment === 'cold_foil' ? 'Cold Foil'
            : foilTreatment === 'inkwash_foil' ? 'Inkwash'
            : foilTreatment === 'glimmer_foil' ? 'Glimmer'
            : foilTreatment === 'iridescent' ? 'Iridescent'
            : 'Foil';
          return (
            <div key={`${card.id}${isFoil ? `-${foilTreatment}` : ''}`} className={`rounded-xl border bg-card p-1.5 cursor-pointer hover:border-primary/30 transition-colors animate-fade-in ${owned ? 'border-primary/30' : 'border-border'}`} onClick={() => setSelectedCard(card)}>
              <div className="relative">
                <CardImage src={card.image_url} alt={card.name} inkColor={card.ink_color as InkColor} enchanted={card.enchanted || false} showGlow className={`aspect-[2/3] ${isFoil ? 'ring-2 ring-primary/40' : ''}`} />
                {isTopPopular && (
                  <span className="absolute top-1 left-1 rounded-full bg-primary px-1.5 py-0.5 text-[8px] font-bold text-primary-foreground font-body">
                    🔥 #{idx + 1}
                  </span>
                )}
                {isFoil && (
                  <span className="absolute top-1 left-1 rounded-full bg-gradient-to-r from-primary to-ink-amethyst px-1.5 py-0.5 text-[8px] font-bold text-primary-foreground font-body shadow">
                    ✨ {foilLabel}
                  </span>
                )}
                {owned && <span className="absolute top-1 right-1 flex h-4 w-4 items-center justify-center rounded-full bg-ink-emerald text-[8px] text-white">✓</span>}
                {wishlisted && !isTopPopular && !isFoil && <span className="absolute top-1 left-1 text-ink-ruby text-xs">❤️</span>}
                {sameRarities > 1 && (
                  <span className="absolute bottom-1 right-1 rounded-full bg-background/80 px-1.5 py-0.5 text-[8px] font-medium text-primary font-body backdrop-blur-sm">
                    {sameRarities} rarities
                  </span>
                )}
              </div>
              <div className="mt-1.5 px-0.5">
                <div className="flex items-center gap-1">
                  <InkDot ink={card.ink_color as InkColor} size="sm" />
                  <span className="truncate text-[10px] font-medium text-foreground font-body">{card.name}</span>
                </div>
                <div className="flex items-center justify-between">
                  {card.subtitle && <span className="text-[9px] text-muted-foreground font-body truncate">{card.subtitle}</span>}
                  <span className="text-[9px] font-medium text-primary font-body ml-auto whitespace-nowrap">{priceLabel}</span>
                </div>
              </div>
            </div>
          );
        };

        if (isLoading) {
          return <div className="mt-2 grid grid-cols-3 md:grid-cols-5 gap-2">{Array.from({ length: 10 }).map((_, i) => <CardSkeleton key={i} />)}</div>;
        }
        if (sorted.length === 0) {
          return (
            <div className="mt-8 py-12 text-center">
              <p className="text-4xl mb-3">🔍</p>
              <p className="text-sm text-muted-foreground font-body">No cards match your search</p>
            </div>
          );
        }

        // POPULAR-BY-SET view: each set rendered as its own section showing the
        // top 8 cards by combined popularity score for that set.
        if (viewMode === 'sets') {
          const bySet = new Map<string, { rep: Card; siblings: Card[] }[]>();
          for (const g of grouped) {
            const key = g.rep.set_name || 'Unknown Set';
            if (!bySet.has(key)) bySet.set(key, []);
            bySet.get(key)!.push(g);
          }
          // Sort sets by total popularity (most-active sets first)
          const sections = Array.from(bySet.entries())
            .map(([setName, groups]) => {
              const ranked = [...groups].sort((a, b) =>
                (popularityScore?.get(normalizeKey(b.rep)) ?? 0) -
                (popularityScore?.get(normalizeKey(a.rep)) ?? 0)
              );
              const score = ranked.slice(0, 8).reduce((acc, g) =>
                acc + (popularityScore?.get(normalizeKey(g.rep)) ?? 0), 0);
              return { setName, groups: ranked, score, total: groups.length };
            })
            .sort((a, b) => b.score - a.score);

          return (
            <div className="mt-3 space-y-6">
              {sections.map(({ setName, groups, total }) => (
                <section key={setName}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="min-w-0">
                      <h3 className="font-display text-base font-semibold text-foreground truncate">{setName}</h3>
                      <p className="text-[10px] text-muted-foreground font-body">
                        🔥 Popular picks · {total} card{total === 1 ? '' : 's'} total
                      </p>
                    </div>
                    <button
                      onClick={() => { setSetFilter(setName); setViewMode('grouped'); }}
                      className="rounded-full border border-border bg-secondary px-3 py-1 text-[10px] font-medium font-body text-secondary-foreground hover:border-primary/40 hover:text-primary transition-colors whitespace-nowrap"
                    >
                      View all »
                    </button>
                  </div>
                  <div className="grid grid-cols-4 md:grid-cols-8 gap-2">
                    {groups.slice(0, 8).map((g, i) => renderCard(g, i, { showRank: true }))}
                  </div>
                </section>
              ))}
            </div>
          );
        }

        return (
          <VirtualizedCardGrid
            items={sorted}
            renderCard={(g, i) => renderCard(g, i, { showRank: sortBy === 'popular' })}
          />
        );
      })()}

      {/* Card detail sheet */}
      {selectedCard && (
        <CardDetailSheet
          card={selectedCard}
          owned={selectedOwned}
          ownedQty={selectedOwnedQty}
          wishlisted={selectedWishlisted}
          isSignedIn={!!user}
          onClose={() => setSelectedCard(null)}
          onAddToCollection={handleAddToCollection}
          onToggleWishlist={handleToggleWishlist}
        />
      )}

      {/* Treatment picker before add */}
      {addingCard && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-background/80 backdrop-blur-sm" onClick={() => setAddingCard(null)}>
          <div className="animate-slide-up w-full max-w-[430px] rounded-t-2xl border border-border bg-card p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-semibold text-foreground">Add {addingCard.name}</h3>
              <button onClick={() => setAddingCard(null)} className="text-muted-foreground"><X className="h-5 w-5" /></button>
            </div>
            <p className="text-xs text-muted-foreground font-body mb-3">
              Pick the printing you own. Each treatment (Normal, Cold Foil, Enchanted, …) is tracked separately so totals reflect what's actually in your binder.
            </p>
            <TreatmentPicker
              cardId={addingCard.id}
              value={addingPrintId}
              onChange={(id, p) => { setAddingPrintId(id); setAddingPrint(p); }}
            />
            <div className="mt-5 flex gap-2">
              <button
                onClick={confirmAdd}
                disabled={addToCollection.isPending}
                className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-primary py-3 text-sm font-medium text-primary-foreground font-body disabled:opacity-50"
              >
                {addToCollection.isPending ? 'Adding…' : `Add ${addingPrint ? treatmentLabel(addingPrint.treatment) : 'Normal'}`}
              </button>
              <button
                onClick={() => setAddingCard(null)}
                className="rounded-lg border border-border bg-secondary px-4 py-3 text-sm font-medium text-secondary-foreground font-body"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
